import {
  type StaffCatalogItemStatus,
  type StaffCatalogItemType,
  type StaffCatalogSearchBody,
  STAFF_CATALOG_SEARCH_BOUNDS as BOUNDS,
  staffCatalogItemStatuses,
  staffCatalogItemTypes,
} from "@lafayette/schemas";

// The search form as plain data. Three pure steps, each tested alone:
//   address (URLSearchParams) <-> values (what the controls hold, as strings)
//   values -> errors (a message for each field that is wrong, in the words of the form)
//   values -> body (exactly what `staffCatalogSearchBodySchema` accepts; never `audience`)
// The address holds the search so a link shares it. It holds only what the form holds: a text, codes,
// numbers and choices. Nothing is stored in the browser.

export const SEARCH_RADIUS_KM = Object.freeze({
  min: BOUNDS.radiusMinMeters / 1000,
  max: BOUNDS.radiusMaxMeters / 1000,
});

export type SearchFormValues = Readonly<{
  text: string;
  types: readonly StaffCatalogItemType[];
  locality: string;
  countryCode: string;
  latitude: string;
  longitude: string;
  radiusKm: string;
  /** Dietary codes as typed, separated by commas or spaces. The checkboxes edit the same string. */
  dietary: string;
  statuses: readonly StaffCatalogItemStatus[];
  limit: string;
  debug: boolean;
}>;

export const emptySearchValues: SearchFormValues = {
  text: "",
  types: [],
  locality: "",
  countryCode: "",
  latitude: "",
  longitude: "",
  radiusKm: "",
  dietary: "",
  statuses: ["PUBLISHED"],
  limit: "",
  debug: false,
};

export type SearchFieldKey =
  | "text"
  | "locality"
  | "countryCode"
  | "latitude"
  | "longitude"
  | "radiusKm"
  | "dietary"
  | "statuses"
  | "limit";

export type SearchFormErrors = Readonly<Partial<Record<SearchFieldKey, string>>>;

/** The order the fields appear in the form: the first error in this order gets the focus. */
const searchFieldOrder: readonly SearchFieldKey[] = [
  "text",
  "locality",
  "countryCode",
  "latitude",
  "longitude",
  "radiusKm",
  "dietary",
  "statuses",
  "limit",
];

const CODE = /^[a-z0-9_]{1,64}$/;
const COUNTRY = /^[A-Z]{2}$/;
const DECIMAL = /^-?\d+(\.\d+)?$/;
const WHOLE = /^\d+$/;

/** The codes in a typed string: split on commas and spaces, no empty entries, no repeats. */
export function splitCodes(raw: string): readonly string[] {
  const codes = raw
    .split(/[\s,]+/)
    .map((code) => code.trim())
    .filter((code) => code !== "");
  return [...new Set(codes)];
}

export const joinCodes = (codes: readonly string[]): string => codes.join(", ");

// --- address --------------------------------------------------------------------------------------------

/** The address keys, in the order they are written. `go` marks a search that was asked for. */
const SEARCH_PARAM = Object.freeze({
  text: "q",
  type: "type",
  locality: "locality",
  country: "country",
  latitude: "lat",
  longitude: "lng",
  radiusKm: "km",
  dietary: "dietary",
  status: "status",
  limit: "limit",
  debug: "debug",
  go: "go",
});

/** A search runs only when the address says so: a bare `/catalog/search` shows the empty form. */
export const isSearchRequested = (params: URLSearchParams): boolean =>
  params.get(SEARCH_PARAM.go) === "1";

const oneOfAll = <T extends string>(allowed: readonly T[], raw: readonly string[]) => ({
  kept: allowed.filter((value) => raw.includes(value)),
  dropped: raw.filter((value) => !(allowed as readonly string[]).includes(value)),
});

export function searchValuesFromParams(params: URLSearchParams): Readonly<{
  values: SearchFormValues;
  ignored: readonly string[];
}> {
  const ignored: string[] = [];
  const types = oneOfAll(staffCatalogItemTypes, params.getAll(SEARCH_PARAM.type));
  if (types.dropped.length > 0) ignored.push(SEARCH_PARAM.type);
  const statuses = oneOfAll(staffCatalogItemStatuses, params.getAll(SEARCH_PARAM.status));
  if (statuses.dropped.length > 0) ignored.push(SEARCH_PARAM.status);
  const values: SearchFormValues = {
    text: params.get(SEARCH_PARAM.text) ?? "",
    types: types.kept,
    locality: params.get(SEARCH_PARAM.locality) ?? "",
    countryCode: params.get(SEARCH_PARAM.country) ?? "",
    latitude: params.get(SEARCH_PARAM.latitude) ?? "",
    longitude: params.get(SEARCH_PARAM.longitude) ?? "",
    radiusKm: params.get(SEARCH_PARAM.radiusKm) ?? "",
    dietary: joinCodes(params.getAll(SEARCH_PARAM.dietary)),
    statuses: statuses.kept.length > 0 ? statuses.kept : emptySearchValues.statuses,
    limit: params.get(SEARCH_PARAM.limit) ?? "",
    debug: params.get(SEARCH_PARAM.debug) === "1",
  };
  return { values, ignored };
}

/** The address of a search. Defaults and empty fields are left out, so a link stays short. */
export function searchParamsFromValues(values: SearchFormValues): URLSearchParams {
  const params = new URLSearchParams();
  const put = (key: string, value: string) => {
    if (value.trim() !== "") params.set(key, value.trim());
  };
  put(SEARCH_PARAM.text, values.text);
  for (const type of staffCatalogItemTypes) {
    if (values.types.includes(type)) params.append(SEARCH_PARAM.type, type);
  }
  put(SEARCH_PARAM.locality, values.locality);
  put(SEARCH_PARAM.country, values.countryCode);
  put(SEARCH_PARAM.latitude, values.latitude);
  put(SEARCH_PARAM.longitude, values.longitude);
  put(SEARCH_PARAM.radiusKm, values.radiusKm);
  for (const code of splitCodes(values.dietary)) params.append(SEARCH_PARAM.dietary, code);
  if (!isDefaultStatuses(values.statuses)) {
    for (const status of staffCatalogItemStatuses) {
      if (values.statuses.includes(status)) params.append(SEARCH_PARAM.status, status);
    }
  }
  put(SEARCH_PARAM.limit, values.limit);
  if (values.debug) params.set(SEARCH_PARAM.debug, "1");
  params.set(SEARCH_PARAM.go, "1");
  return params;
}

const isDefaultStatuses = (statuses: readonly StaffCatalogItemStatus[]) =>
  statuses.length === 1 && statuses[0] === "PUBLISHED";

// --- validation -----------------------------------------------------------------------------------------

const isDecimal = (raw: string) => DECIMAL.test(raw.trim());

export function validateSearchValues(values: SearchFormValues): SearchFormErrors {
  const errors: Partial<Record<SearchFieldKey, string>> = {};

  if (values.text.length > BOUNDS.textChars) {
    errors.text = `Use at most ${BOUNDS.textChars} characters. The text has ${values.text.length}.`;
  }

  if (values.locality.trim().length > BOUNDS.locality) {
    errors.locality = `Use at most ${BOUNDS.locality} characters.`;
  }
  const country = values.countryCode.trim();
  if (country !== "" && !COUNTRY.test(country.toUpperCase())) {
    errors.countryCode = "Use two letters, for example ES.";
  }

  // The point and the radius go together: all three or none.
  const lat = values.latitude.trim();
  const lng = values.longitude.trim();
  const km = values.radiusKm.trim();
  if (lat !== "" || lng !== "" || km !== "") {
    const missing = "Fill in latitude, longitude and radius together, or leave all three empty.";
    if (lat === "") errors.latitude = missing;
    else if (!isDecimal(lat) || Math.abs(Number(lat)) > 90) {
      errors.latitude = "Latitude is a number from -90 to 90.";
    }
    if (lng === "") errors.longitude = missing;
    else if (!isDecimal(lng) || Math.abs(Number(lng)) > 180) {
      errors.longitude = "Longitude is a number from -180 to 180.";
    }
    if (km === "") errors.radiusKm = missing;
    else if (
      !isDecimal(km) ||
      Number(km) < SEARCH_RADIUS_KM.min ||
      Number(km) > SEARCH_RADIUS_KM.max
    ) {
      errors.radiusKm = `Radius is a number from ${SEARCH_RADIUS_KM.min} to ${SEARCH_RADIUS_KM.max} km.`;
    }
  }

  const codes = splitCodes(values.dietary);
  if (codes.length > BOUNDS.dietary) {
    errors.dietary = `Choose at most ${BOUNDS.dietary} dietary tags.`;
  } else if (codes.some((code) => !CODE.test(code))) {
    errors.dietary = "A dietary code uses lowercase letters, digits and underscores only.";
  }

  if (values.statuses.length === 0) errors.statuses = "Choose at least one status.";

  const limit = values.limit.trim();
  if (limit !== "") {
    if (!WHOLE.test(limit) || Number(limit) < 1 || Number(limit) > BOUNDS.limitMax) {
      errors.limit = `Limit is a whole number from 1 to ${BOUNDS.limitMax}.`;
    }
  }

  return errors;
}

export const firstErrorField = (errors: SearchFormErrors): SearchFieldKey | null =>
  searchFieldOrder.find((key) => errors[key] !== undefined) ?? null;

// --- request --------------------------------------------------------------------------------------------

/** The request body. Call it only for values with no error. It leaves out every default. */
export function searchBodyFromValues(values: SearchFormValues): StaffCatalogSearchBody {
  const body: {
    -readonly [K in keyof StaffCatalogSearchBody]: StaffCatalogSearchBody[K];
  } = {};

  const text = values.text.trim();
  if (text !== "") body.text = text;
  const types = staffCatalogItemTypes.filter((type) => values.types.includes(type));
  if (types.length > 0) body.types = types;

  const locality = values.locality.trim();
  const countryCode = values.countryCode.trim().toUpperCase();
  if (locality !== "" || countryCode !== "") {
    body.location = {
      ...(countryCode === "" ? {} : { countryCode }),
      ...(locality === "" ? {} : { locality }),
    };
  }

  if (values.latitude.trim() !== "") {
    body.near = {
      latitude: Number(values.latitude),
      longitude: Number(values.longitude),
      radiusMeters: Math.round(Number(values.radiusKm) * 1000),
    };
  }

  const dietary = splitCodes(values.dietary);
  if (dietary.length > 0) body.dietary = [...dietary];

  if (!isDefaultStatuses(values.statuses)) {
    body.includeStatuses = staffCatalogItemStatuses.filter((status) =>
      values.statuses.includes(status),
    );
  }
  const limit = values.limit.trim();
  if (limit !== "" && Number(limit) !== BOUNDS.limitDefault) body.limit = Number(limit);
  if (values.debug) body.debug = true;
  return body;
}
